import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, IconButton, Paper, Popper, Stack, Typography } from '@mui/material';
import { GridRenderCellParams } from '@mui/x-data-grid-pro';
import { DataTable } from '@pihealth/common-ui-library';
import React from 'react';
import { useUser } from 'src/components/hooks/useUser';
import { usePreferedDateFormat } from 'src/util/DateFormatter';
import { GridToolbarExport } from '@mui/x-data-grid-pro';
import moment from 'moment';
interface GridCellExpandProps {
  value: string;
  width: number;
}

const GridCellExpand = React.memo(function GridCellExpand(props: GridCellExpandProps) {
  const { width, value } = props;
  const wrapper: any = React.useRef<HTMLDivElement | null>(null);
  const cellDiv = React.useRef(null);
  const cellValue: any = React.useRef(null);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [showFullCell, setShowFullCell] = React.useState(false);
  const [showPopper, setShowPopper] = React.useState(false);
  function isOverflown(element: Element): boolean {
    return element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth;
  }
  const handleMouseEnter = () => {
    const isCurrentlyOverflown = isOverflown(cellValue.current);
    setShowPopper(isCurrentlyOverflown);
    setAnchorEl(cellDiv.current);
    setShowFullCell(true);
  };
  const handleMouseLeave = () => {
    setShowFullCell(false);
  };
  React.useEffect(() => {
    if (!showFullCell) {
      return undefined;
    }
    function handleKeyDown(nativeEvent: KeyboardEvent) {
      // IE11, Edge (prior to using Bink?) use 'Esc'
      if (nativeEvent.key === 'Escape' || nativeEvent.key === 'Esc') {
        setShowFullCell(false);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [setShowFullCell, showFullCell]);
  return (
    <Box
      ref={wrapper}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        alignItems: 'center',
        lineHeight: '24px',
        width: '100%',
        height: '100%',
        position: 'relative',
        display: 'flex'
      }}
    >
      <Box
        ref={cellDiv}
        sx={{
          height: '100%',
          width,
          display: 'block',
          position: 'absolute',
          top: 0
        }}
      />
      <Box ref={cellValue} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {value}
      </Box>
      {showPopper && (
        <Popper open={showFullCell && anchorEl !== null} anchorEl={anchorEl} style={{ width, marginLeft: -17 }}>
          <Paper elevation={6} style={{ minHeight: wrapper.current?.offsetHeight - 3 }}>
            <Typography variant="body1" style={{ padding: 12, wordBreak: 'break-word' }}>
              {value}
            </Typography>
          </Paper>
        </Popper>
      )}
    </Box>
  );
});
function renderCellExpand(params: GridRenderCellParams<string>) {
  return <GridCellExpand value={params.value || ''} width={params.colDef.computedWidth} />;
}

const DataGridIssueLog: React.FunctionComponent<any> = (props: any) => {
  const {
    rows,
    isLoading,
    selectedSite,
    currentIssueLevel,
    selectionModel,
    handleSelectionModelChange,
    CustomFooterButtons,
    issueLogPermissions,
    handelEdit,
    handleDeleteOpen,
    apiRef,
    copyToSponsorIssuePermission,
    pageSize,
    setPageSize,
    rowsCount,
    pageNumber,
    setPageNumber,
    setFilterObject
  } = props;
  const { preferedDateFormat, userPreferedDateFormat } = usePreferedDateFormat();
  const { user, email } = useUser();
  const currentUserWithEmail = `${user} (${email})`;

  const formatDate = (params: any) => {
    return preferedDateFormat(params.value);
  };

  const columns = [
    {
      field: 'issue_no',
      headerName: 'Issue Number',
      width: 100,
      renderCell: (params: any) => {
        return (
          <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <span className="artNameinTd">{params.formattedValue}</span>
            {(params.row.action_due < new Date().toLocaleDateString('en-CA') &&
              params.row.action_completion_dt === null && <WarningAmberIcon color="error" />) ||
              (params.row.decision_due_dt < new Date().toLocaleDateString('en-CA') &&
                params.row.decision_comp_dt === null && <WarningAmberIcon color="error" />)}
          </div>
        );
      }
    },
    {
      field: 'study_id',
      headerName: 'Study',
      width: 120,
      renderCell: renderCellExpand
    },
    {
      field: 'country',
      headerName: 'Country',
      minWidth: 140,
      renderCell: renderCellExpand
    },
    {
      field: 'site_id',
      headerName: 'Site',
      minWidth: 120,
      renderCell: renderCellExpand
    },
    {
      field: 'subject_id',
      headerName: 'Subject ID',
      minWidth: 120,
      renderCell: renderCellExpand
    },
    {
      field: 'category',
      headerName: 'Category',
      width: 150,
      renderCell: renderCellExpand
    },
    {
      field: 'severity',
      headerName: 'Severity',
      width: 100
    },
    {
      field: 'issue_class',
      headerName: 'Issue Class',
      width: 80
    },
    {
      field: 'reported_irb',
      headerName: 'Reported to IRB/IEC?',
      width: 120,
      type: 'boolean',
      renderCell: (params: any) => (
        <Stack direction="row" spacing={1}>
          {params.row?.reported_irb ? (
            <IconButton title="Reported">
              <CheckCircleRoundedIcon color="success" />
            </IconButton>
          ) : null}
        </Stack>
      )
    },
    {
      field: 'date_irb',
      headerName: 'Date Reported to IRB',
      width: 120,
      type: 'date',
      valueGetter: formatDate
    },
    {
      field: 'issue',
      headerName: 'Issue',
      width: 250,
      renderCell: renderCellExpand
    },
    {
      field: 'dt_occur',
      headerName: 'Date of Issue Occurrence',
      width: 120,
      type: 'date',
      valueGetter: formatDate
    },
    {
      field: 'dt_report',
      headerName: 'Date of Issue Reporting',
      width: 120,
      type: 'date',
      valueGetter: formatDate
    },
    {
      field: 'action_req',
      headerName: 'Action Required?',
      width: 100,
      filterable: false
    },
    {
      field: 'action_des',
      headerName: 'Action Description',
      width: 140,
      renderCell: renderCellExpand
    },
    {
      field: 'action_taken',
      headerName: 'Action Taken',
      width: 120,
      renderCell: renderCellExpand
    },
    {
      field: 'action_assigned_to',
      headerName: 'Action Assigned To',
      width: 140,
      renderCell: renderCellExpand
    },
    {
      field: 'action_due',
      headerName: 'Action Due Date',
      width: 100,
      type: 'date',
      valueGetter: formatDate
    },
    {
      field: 'action_completion_dt',
      headerName: 'Date of Action Completion',
      width: 120,
      type: 'date',
      valueGetter: formatDate
    },
    {
      field: 'decision_req',
      headerName: 'Decision Required?',
      width: 100,
      filterable: false
    },
    {
      field: 'decision',
      headerName: 'Decisions',
      width: 140,
      renderCell: renderCellExpand
    },
    {
      field: 'decision_update',
      headerName: 'Decision Update',
      width: 140,
      renderCell: renderCellExpand
    },
    {
      field: 'decision_assigned_to',
      headerName: 'Decision Assigned To',
      width: 140,
      renderCell: renderCellExpand
    },
    {
      field: 'decision_due_dt',
      headerName: 'Decision Due Date',
      width: 100,
      type: 'date',
      valueGetter: formatDate
    },
    {
      field: 'decision_comp_dt',
      headerName: 'Date of Decision Completion',
      width: 140,
      expandable: true,
      type: 'date',
      valueGetter: formatDate
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      sortable: false,
      width: 80,
      print: false,
      renderCell: (params: any) => {
        const filterActionUsers =
          params.row.action_assigned_to.length <= 0
            ? params.row.action_assigned_to
            : params.row.action_assigned_to.filter((name: string) => name === currentUserWithEmail);
        const filterDecisionUsers =
          params.row.decision_assigned_to.length <= 0
            ? params.row.decision_assigned_to
            : params.row.decision_assigned_to.filter((name: string) => name === currentUserWithEmail);
        return (
          <Stack direction="row" spacing={0}>
            {(params.row.created_by === user ? issueLogPermissions.update : issueLogPermissions.updateOther) ||
            (issueLogPermissions.updateAction && (filterActionUsers.length > 0 ? true : false)) ||
            (issueLogPermissions.updateDecision && (filterDecisionUsers.length > 0 ? true : false)) ? (
              <IconButton color="primary" onClick={() => handelEdit(params.row)} title="Edit">
                <EditIcon />
              </IconButton>
            ) : (
              ''
            )}
            {(params.row.created_by === user ? issueLogPermissions.update : issueLogPermissions.deleteOther) ? (
              <IconButton color="primary" onClick={() => handleDeleteOpen(params.row)} title="Delete">
                <DeleteIcon />
              </IconButton>
            ) : (
              ''
            )}
          </Stack>
        );
      }
    }
  ];
  const dataTableProps: any = {
    apiRef,
    sx: {
      '& .MuiDataGrid-columnHeaderTitle': {
        whiteSpace: 'break-spaces !important',
        lineHeight: 1.3,
        '&:hover': {
          textOverflow: 'ellipsis'
        }
      }
    },
    filterMode: 'server'
  };
  const definedCustomDatGridExport = (exportFields: any = {}) => {
    return (
      <GridToolbarExport
        csvOptions={{
          fileName: `${`Issue Log`} ${moment(new Date()).format(
            `${(userPreferedDateFormat.includes('/')
              ? userPreferedDateFormat.replaceAll('/', '_')
              : userPreferedDateFormat.replaceAll('-', '_')
            ).toUpperCase()} hh_mm A`
          )}`
        }}
      />
    );
  };
  const filterValues = (data: any) => {
    data.filters.forEach((item: { operation: string; name: string; value: string | boolean | undefined }) => {
      if (item.name === 'reported_irb') {
        if (item.value === undefined || item.value === '') {
          item.value = undefined;
          item.operation = 'is_not';
        } else {
          item.value = item.value === 'true';
        }
      }
    });
    setFilterObject(data);
  };

  return (
    <>
      <DataTable
        rows={rows}
        columns={columns}
        hasDefinedColumns
        loading={isLoading}
        datePreference={userPreferedDateFormat}
        paging
        rowsCount={rowsCount}
        pageNumber={pageNumber}
        pageSize={pageSize}
        setPageSize={setPageSize}
        setPageNumber={setPageNumber}
        paginationMode="server"
        className="studylogDatagrid"
        exportAll
        height="74vh"
        disableSelectionOnClick
        checkboxSelection={
          selectedSite !== null ? (copyToSponsorIssuePermission && currentIssueLevel === 'SITE' ? true : false) : false
        }
        selectionModel={selectionModel}
        onRowsSelection={handleSelectionModelChange}
        CustomFooter={CustomFooterButtons}
        checkboxSelectionVisibleOnly
        dataTableProps={dataTableProps}
        pinnedColumns={{
          right: ['actions']
        }}
        hasDefinedDataGridToolbarExport
        definedGridToolbarExport={definedCustomDatGridExport}
        filterValues={(data: any) => filterValues(data)}
      />
    </>
  );
};
export default DataGridIssueLog;
